import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { IMaskInputProps } from 'react-imask';
import {
  useForm,
  useFieldArray,
  Controller,
  FieldError,
} from 'react-hook-form';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  useTheme,
  Theme,
  Button,
  Alert,
  Autocomplete,
  Chip,
  IconButton,
  FormControl,
  FormLabel,
  Select,
  Option,
  FormHelperText,
  Radio,
  RadioGroup,
  LinearProgress,
} from '@mui/joy';
import HelloSign from 'hellosign-embedded';
import { tss } from 'tss-react';
import { ControlledInput } from 'src/components/ControlledInput';
import CurrencyInput from 'src/components/CurrencyInput';
import {
  EmailOutlined, LockOutlined, Add as AddIcon, Close as CloseIcon,
  InfoOutlined,
  Delete,
  Download,
  Draw,
} from '@mui/icons-material';
import {
  ListingBuyerType,
  Type24EnumsListingBuyerType,
  ListingBuyerNDARequestFinancingStatus,
  Type24EnumsListingBuyerNDARequestFinancingStatus,
  ListingBuyerNDAStatus,
} from 'src/generated/api';
import constants from 'src/constants';
import { getAPI } from 'src/api';
import { getConfig } from 'src/config';
import { getSessionReplayUrl, logEvent } from 'src/analytics';
import PDFViewer from 'src/components/PDFViewer';
import PhoneMaskAdapter, { PhoneMaskAdapterCustomProps } from 'src/components/PhoneMask';
import { ListingAuthContext } from '../ListingAuthContext';
import { loggingEvents } from '../loggingEvents';
import { revenueRangeOptions, ebitdaRangeOptions } from './LogInModal';

interface ContactModalProps {
  id: string;
  ndaRequestStatus: ListingBuyerNDAStatus | null;
  onClose: () => void;
}

interface EmailFormData {
  email: string;
}

interface OtpFormData {
  otp: string;
}

interface UserInfoFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  type: string;
  linkedinUrl: string;
  firmName: string;
  jobTitle: string;
  criteriaEbitdaRange: string;
  criteriaDealSizeMin: number;
  criteriaDealSizeMax: number;
  criteriaIndustryFocus: string[];
  criteriaRevenueRange: string;
  previousAcquisitions: { value: string }[];
  hasIndustryFocus: boolean | null;
  hasCompletedAcquisitions: boolean | null;
  criteriaIndustryFocusInput: string;
}

interface NdaRequestFormData {
  hasRelevantExperience: boolean | null;
  relevantExperience: string;
  financingStatus: ListingBuyerNDARequestFinancingStatus | null;
  financingStatusExplanation: string;
  ownerPostTransactionExpectation: string;
  referrer: string;
}

const listingBuyerTypeOptions = [
  { key: Type24EnumsListingBuyerType.PRIVATE_EQUITY, label: 'Private Equity (PE) Firm' },
  { key: Type24EnumsListingBuyerType.STRATEGIC, label: 'Corporate / Strategic Buyer' },
  { key: Type24EnumsListingBuyerType.SEARCH_FUND, label: 'Independent Sponsor / Search Fund' },
  { key: Type24EnumsListingBuyerType.INDIVIDUAL, label: 'Individual Investor' },
];

const PhoneMaskAdapterRef = React.forwardRef<
HTMLInputElement,
PhoneMaskAdapterCustomProps & IMaskInputProps<HTMLInputElement>
>(
  PhoneMaskAdapter,
);

const useStyles = tss
  .withParams<{ theme: Theme }>()
  .create(({ theme }) => ({
    modal: {
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      gap: 0,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.spacing(1),
      gap: theme.spacing(3),
      overflowY: 'auto',
      overflowX: 'hidden',
      '&::-webkit-scrollbar': {
        width: '8px',
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.neutral[400],
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      maxWidth: 700,
      width: '100%',
      boxSizing: 'border-box',
    },
    thinForm: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.spacing(2),
      gap: theme.spacing(2),
      width: 400,
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
      width: '100%',
    },
    group: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    fullWidth: {
      width: '100%',
    },
    previousAcquisition: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
      alignItems: 'flex-end',
      width: '100%',
    },
    pdfContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    progressContainer: {
      marginTop: theme.spacing(2),
    },
    stepText: {
      marginLeft: 'auto',
      textAlign: 'right',
    },
    pdfActions: {
      display: 'flex',
      gap: theme.spacing(2),
    },
  }));

type Step = 'email' | 'otp' | 'userInfo' | 'criteriaInfo' | 'ndaRequestInfo' | 'previewNda';

const stepOrder: Record<Step, number> = {
  email: 0,
  otp: 1,
  userInfo: 2,
  criteriaInfo: 3,
  ndaRequestInfo: 4,
  previewNda: 5,
};

const financingStatusOptions = [
  { value: Type24EnumsListingBuyerNDARequestFinancingStatus.YES, label: 'Yes' },
  { value: Type24EnumsListingBuyerNDARequestFinancingStatus.IN_PROGRESS, label: 'In Progress' },
  { value: Type24EnumsListingBuyerNDARequestFinancingStatus.NO, label: 'No' },
];

const ownerPostTransactionOptions = [
  { value: 'no_involvement', label: 'No involvement' },
  { value: 'short_term_transition', label: 'Short-term transition (0–6 months)' },
  { value: 'medium_term_transition', label: 'Medium-term involvement (6–12 months)' },
  { value: 'long_term_transition', label: 'Long-term involvement (more than 12 months)' },
  { value: 'open_to_discussion', label: 'Open to discussion' },
];

const referrerMap: Record<string, string> = {
  'linkedin.com': 'linkedin',
  'network.axial.net': 'axial',
  'searchfunder.com': 'searchfunder',
} as const;

const hellosignClient = new HelloSign({
  clientId: constants.helloSignClientId,
});

export function NDAModal({
  id,
  ndaRequestStatus,
  onClose,
}: ContactModalProps) {
  const theme = useTheme();
  const [isLoadingSignUrl, setIsLoadingSignUrl] = useState(false);
  const { refetch, user } = useContext(ListingAuthContext);
  const { classes } = useStyles({ theme });
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const config = getConfig();
  const pdfUrl = `${config.hosts.api}/listings/${id}/nda_file`;

  const emailForm = useForm<EmailFormData>();
  const otpForm = useForm<OtpFormData>();
  const userInfoForm = useForm<UserInfoFormData>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
      type: user?.type || '',
      linkedinUrl: user?.linkedinUrl || '',
      firmName: user?.firmName || '',
      jobTitle: user?.jobTitle || '',
      hasIndustryFocus: null,
      hasCompletedAcquisitions: null,
      criteriaIndustryFocus: [],
      criteriaIndustryFocusInput: '',
      previousAcquisitions: [],
    },
  });

  const ndaRequestForm = useForm<NdaRequestFormData>({
    mode: 'onSubmit',
    defaultValues: {
      hasRelevantExperience: null,
      relevantExperience: '',
      financingStatus: null,
      financingStatusExplanation: '',
      ownerPostTransactionExpectation: '',
      referrer: (referrerMap[document.referrer as keyof typeof referrerMap] || '') as string,
    },
  });

  const api = getAPI();

  const handleEmailSubmit = useCallback(async (data: EmailFormData) => {
    logEvent({
      name: loggingEvents.auth.submitEmail,
      type: 'response',
      extra: { listingId: id },
    });
    await api.listings.sendOneTimeCode({ email: data.email });
    setEmail(data.email);
    setStep('otp');
  }, [api, id]);

  const handleOtpSubmit = useCallback(async (data: OtpFormData) => {
    logEvent({
      name: loggingEvents.auth.submitOtp,
      type: 'response',
      extra: { listingId: id },
    });
    try {
      await api.listings.verifyOneTimeCode({ email, code: data.otp });
      await refetch();
      setStep('userInfo');
    } catch (err) {
      otpForm.setError('otp', { message: 'Invalid one-time code' });
    }
  }, [api, email, otpForm, refetch, id]);

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = userInfoForm;

  const handleUserInfoSubmit = useCallback(async (data: UserInfoFormData) => {
    logEvent({
      name: loggingEvents.auth.submitUserInfo,
      type: 'response',
      extra: { listingId: id },
    });
    await api.listings.updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      type: data.type as ListingBuyerType,
      criteriaEbitdaRange: data.criteriaEbitdaRange,
      criteriaDealSizeMin: data.criteriaDealSizeMin,
      criteriaDealSizeMax: data.criteriaDealSizeMax,
      criteriaRevenueRange: data.criteriaRevenueRange,
      linkedinUrl: data.linkedinUrl,
      firmName: data.firmName,
      jobTitle: data.jobTitle,
      criteriaIndustryFocus: data.hasIndustryFocus ? data.criteriaIndustryFocus : [],
      previousAcquisitions: data.hasCompletedAcquisitions ?
        data.previousAcquisitions.map((value) => value.value.trim()) : [],
    });
    await refetch();
    setStep('ndaRequestInfo');
  }, [api, refetch, id]);

  const {
    fields: previousAcquisitionsFields,
    append: appendPreviousAcquisition,
    remove: removePreviousAcquisition,
  } = useFieldArray({
    control: userInfoForm.control,
    name: 'previousAcquisitions',
  });

  useEffect(() => {
    if (user) {
      if (ndaRequestStatus === 'REQUESTED') {
        setStep('previewNda');
      } else if (
        user.firstName && user.lastName && user.phoneNumber &&
        user.type && user.criteriaEbitdaRange && user.criteriaDealSizeMin &&
        user.criteriaDealSizeMax && user.criteriaIndustryFocus &&
        user.criteriaRevenueRange && user.previousAcquisitions
      ) {
        setStep('ndaRequestInfo');
      } else {
        setStep('userInfo');
        // Pre-fill the form with existing user data
        userInfoForm.reset({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phoneNumber: user.phoneNumber || '',
          type: user.type || '',
          linkedinUrl: user.linkedinUrl || '',
          firmName: user.firmName || '',
          jobTitle: user.jobTitle || '',
          criteriaEbitdaRange: user.criteriaEbitdaRange || '',
          criteriaDealSizeMin: user.criteriaDealSizeMin || 1000000,
          criteriaDealSizeMax: user.criteriaDealSizeMax || 100000000,
          criteriaIndustryFocus: user.criteriaIndustryFocus || [],
          criteriaRevenueRange: user.criteriaRevenueRange || '',
          previousAcquisitions: user.previousAcquisitions?.map((value) => ({ value })),
          hasIndustryFocus: null,
          hasCompletedAcquisitions: null,
          criteriaIndustryFocusInput: '',
        });
      }
    }
  }, [user, userInfoForm, ndaRequestStatus]);

  const showFirmField = userInfoForm.watch('type') === Type24EnumsListingBuyerType.PRIVATE_EQUITY ||
    userInfoForm.watch('type') === Type24EnumsListingBuyerType.STRATEGIC;

  const onUserInfoNext = handleSubmit(() => {
    if (Object.keys(errors).length === 0) {
      setStep('criteriaInfo');
    }
  });

  const handleNdaRequestSubmit = useCallback(async (data: NdaRequestFormData) => {
    logEvent({
      name: loggingEvents.ndaModal.submitRequestInfo,
      type: 'response',
      extra: { listingId: id },
    });
    await api.listings.createNdaRequest(id, {
      relevantExperience: data.hasRelevantExperience ? data.relevantExperience : '',
      financingStatus: data.financingStatus!,
      financingStatusExplanation: data.financingStatusExplanation,
      ownerPostTransactionExpectation: data.ownerPostTransactionExpectation,
      referrer: data.referrer,
    });
    setStep('previewNda');
  }, [api, id]);

  const handleDownloadNda = useCallback(() => {
    logEvent({
      name: loggingEvents.ndaModal.downloadNda,
      type: 'click',
      extra: { listingId: id },
    });
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  }, [pdfUrl, id]);

  const handleSignNda = useCallback(async () => {
    logEvent({
      name: loggingEvents.ndaModal.signNdaStart,
      type: 'click',
      extra: { listingId: id },
    });
    setIsLoadingSignUrl(true);
    const response = await api.listings.getNdaSignUrl(id);
    const posthogUrl = getSessionReplayUrl();
    hellosignClient.on('sign', async () => {
      logEvent({
        name: loggingEvents.ndaModal.signNdaSuccess,
        type: 'response',
        extra: { listingId: id },
      });
      await api.listings.onNdaSigned(id, {
        sessionReplayUrl: posthogUrl,
      });
    });
    hellosignClient.on('close', () => {
      onClose();
    });
    hellosignClient.open(response.data.helloSignUrl, {
      skipDomainVerification: config.isDev,
    });
    logEvent({
      name: loggingEvents.ndaModal.signNdaLoaded,
      type: 'response',
      extra: { listingId: id },
    });
  }, [api, id, onClose, config.isDev]);

  return (
    <Modal open onClose={onClose}>
      <ModalDialog className={classes.modal}>
        <ModalClose />
        <div className={classes.progressContainer}>
          <LinearProgress
            variant='solid'
            determinate
            value={(stepOrder[step] / (Object.keys(stepOrder).length - 1)) * 100}
          />
          <Typography level='body-sm' className={classes.stepText}>
            Step {stepOrder[step] + 1} of {Object.keys(stepOrder).length}
          </Typography>
        </div>
        {!isLoadingSignUrl && (
          <>
            {step === 'email' && (
              <form
                onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
                className={classes.thinForm}
              >
                <Typography level='h4'>Submit an NDA</Typography>
                <Typography level='body-md'>
                  To access confidential information about this business opportunity,
                  please complete our NDA process. This should take about 2-3 minutes.
                </Typography>
                <ControlledInput
                  label="Email Address"
                  name="email"
                  inputProps={{
                    placeholder: 'Enter your work email',
                    onFocus: () => logEvent({
                      name: loggingEvents.auth.focusEmailInput,
                      type: 'click',
                      extra: { listingId: id },
                    }),
                  }}
                  control={emailForm.control}
                  required
                  StartDecorator={EmailOutlined}
                />
                <Button type="submit">Next</Button>
              </form>
            )}
            {step === 'otp' && (
              <form
                onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
                className={classes.thinForm}
              >
                <Typography level='h4'>Verify Your Email</Typography>
                {
                  otpForm.formState.errors.otp && (
                    <Alert color="danger">
                      {otpForm.formState.errors.otp.message}
                    </Alert>
                  )
                }
                <Typography level='body-md'>
                  Enter the 6-digit code sent to your email.
                  This helps us ensure the security of sensitive information.
                </Typography>
                <ControlledInput
                  label="One-Time Code"
                  name="otp"
                  control={otpForm.control}
                  required
                  StartDecorator={LockOutlined}
                  inputProps={{
                    placeholder: 'Enter the 6-digit code',
                    inputMode: 'numeric',
                    onFocus: () => logEvent({
                      name: loggingEvents.auth.focusOtpInput,
                      type: 'click',
                      extra: { listingId: id },
                    }),
                  }}
                  pattern="[0-9]{6}"
                />
                <Button type="submit">Submit Code</Button>
              </form>
            )}
            {step === 'userInfo' && (
              <>
                <form onSubmit={onUserInfoNext} className={classes.form}>
                  <div>
                    <Typography level='h4'>Complete Your Profile</Typography>
                    <Typography level='body-md'>
                      Please provide some basic information about yourself.
                      This helps us tailor opportunities to your interests
                    </Typography>
                  </div>
                  <div className={classes.row}>
                    <ControlledInput
                      label="First Name"
                      name="firstName"
                      control={userInfoForm.control}
                      className={classes.fullWidth}
                      required
                      error={errors.firstName}
                    />
                    <ControlledInput
                      label="Last Name"
                      name="lastName"
                      control={userInfoForm.control}
                      className={classes.fullWidth}
                      required
                      error={errors.lastName}
                    />
                  </div>
                  <div className={classes.row}>
                    <ControlledInput
                      label="Phone Number"
                      name="phoneNumber"
                      required
                      control={userInfoForm.control}
                      className={classes.fullWidth}
                      error={errors.phoneNumber}
                      inputProps={{
                        slotProps: {
                          input: {
                            component: PhoneMaskAdapterRef,
                          },
                        },
                      }}
                    />
                    <ControlledInput
                      label="LinkedIn URL (Optional)"
                      name="linkedinUrl"
                      control={userInfoForm.control}
                      className={classes.fullWidth}
                      error={errors.linkedinUrl}
                    />
                  </div>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: 'This field is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl error={!!error}>
                        <FormLabel>Investor Type</FormLabel>
                        <Select
                          {...field}
                          size="lg"
                          value={field.value || ''}
                          onChange={(_, newValue) => field.onChange(newValue)}
                        >
                          {listingBuyerTypeOptions.map((option) => (
                            <Option key={option.key} value={option.key}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                        {error && (
                          <FormHelperText>
                            <InfoOutlined />
                            {error.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  {showFirmField && (
                    <div className={classes.row}>
                      <ControlledInput
                        label="Firm Name"
                        name="firmName"
                        control={userInfoForm.control}
                        required={showFirmField}
                        className={classes.fullWidth}
                        error={errors.firmName}
                      />
                      <ControlledInput
                        label="Job Title"
                        name="jobTitle"
                        control={userInfoForm.control}
                        required={showFirmField}
                        className={classes.fullWidth}
                        error={errors.jobTitle}
                      />
                    </div>
                  )}
                  <Button type="submit">
                    Next
                  </Button>
                </form>
              </>
            )}
            {step === 'criteriaInfo' && (
              <>
                <Typography level='h4'>Investment Criteria</Typography>
                <Typography level='body-md'>
                  Help us understand what types of businesses you're interested in acquiring.
                </Typography>
                <form
                  onSubmit={userInfoForm.handleSubmit(handleUserInfoSubmit)}
                  className={classes.form}
                >
                  <FormControl error={!!errors.criteriaRevenueRange}>
                    <FormLabel>Target Revenue Range</FormLabel>
                    <Controller
                      name="criteriaRevenueRange"
                      control={control}
                      rules={{ required: 'This is required' }}
                      render={({ field }) => (
                        <RadioGroup {...field}>
                          {revenueRangeOptions.map((option) => (
                            <Radio key={option.value} value={option.value} label={option.label} />
                          ))}
                        </RadioGroup>
                      )}
                    />
                    {errors.criteriaRevenueRange && (
                      <FormHelperText>
                        <InfoOutlined />
                        {(errors.criteriaRevenueRange as FieldError).message}
                      </FormHelperText>
                    )}
                  </FormControl>
                  <FormControl error={!!errors.criteriaEbitdaRange}>
                    <FormLabel>Target EBITDA Range</FormLabel>
                    <Controller
                      name="criteriaEbitdaRange"
                      control={control}
                      rules={{ required: 'This field is required' }}
                      render={({ field }) => (
                        <RadioGroup {...field}>
                          {ebitdaRangeOptions.map((option) => (
                            <Radio key={option.value} value={option.value} label={option.label} />
                          ))}
                        </RadioGroup>
                      )}
                    />
                    {errors.criteriaEbitdaRange && (
                      <FormHelperText>
                        <InfoOutlined />
                        {(errors.criteriaEbitdaRange as FieldError).message}
                      </FormHelperText>
                    )}
                  </FormControl>
                  <div className={classes.group}>
                    <Typography level='body-md'>
                      <b>
                        Preferred Deal Size
                      </b>
                    </Typography>
                    <div className={classes.row}>
                      <CurrencyInput
                        label="Minimum ($)"
                        name="criteriaDealSizeMin"
                        control={userInfoForm.control}
                        required
                        inputProps={{
                          inputMode: 'numeric',
                        }}
                      />
                      <CurrencyInput
                        label="Maximum ($)"
                        name="criteriaDealSizeMax"
                        control={userInfoForm.control}
                        required
                        inputProps={{
                          inputMode: 'numeric',
                        }}
                      />
                    </div>
                  </div>
                  <div className={classes.group}>
                    <Typography level='body-md'>
                      <b>Do you have an industry focus?</b>
                    </Typography>
                    <FormControl error={!!errors.hasIndustryFocus}>
                      <Controller
                        name="hasIndustryFocus"
                        control={control}
                        rules={{
                          validate: (value) => value !== null || 'This is required',
                        }}
                        render={({ field }) => (
                          <RadioGroup
                            orientation="horizontal"
                            value={field.value === null ? '' : field.value ? 'yes' : 'no'}
                            onChange={(event) => {
                              const newValue = event.target.value === 'yes';
                              field.onChange(newValue);
                            }}
                          >
                            <Radio value="yes" label="Yes" />
                            <Radio value="no" label="No" />
                          </RadioGroup>
                        )}
                      />
                      {errors.hasIndustryFocus && (
                        <FormHelperText>
                          <InfoOutlined />
                          {errors.hasIndustryFocus.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                    {userInfoForm.watch('hasIndustryFocus') && (
                      <FormControl error={!!errors.criteriaIndustryFocus}>
                        <FormLabel>
                          Please specify your industry focus (e.g. “SaaS/Software”,
                          “facilities management”, “residential services”)
                        </FormLabel>
                        <Controller
                          name="criteriaIndustryFocus"
                          control={control}
                          rules={{ required: 'Please enter at least one industry focus' }}
                          render={({ field }) => (
                            <Autocomplete
                              multiple
                              freeSolo
                              options={[]}
                              value={field.value}
                              onChange={(_, newValue) => field.onChange(newValue)}
                              inputValue={userInfoForm.watch('criteriaIndustryFocusInput') || ''}
                              onInputChange={(_, newInputValue) => {
                                userInfoForm.setValue('criteriaIndustryFocusInput', newInputValue);
                              }}
                              onBlur={() => {
                                const inputValue = userInfoForm.watch('criteriaIndustryFocusInput').trim();
                                if (inputValue && !field.value.includes(inputValue)) {
                                  field.onChange([...field.value, inputValue]);
                                  userInfoForm.setValue('criteriaIndustryFocusInput', '');
                                }
                              }}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    variant="soft"
                                    color="warning"
                                    {...getTagProps({ index })}
                                    key={option}
                                    endDecorator={
                                      <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        onClick={() => {
                                          const newValue = field.value
                                            .filter((_, i) => i !== index);
                                          field.onChange(newValue);
                                        }}
                                      >
                                        <CloseIcon />
                                      </IconButton>
                                    }
                                  >
                                    {option}
                                  </Chip>
                                ))
                              }
                            />
                          )}
                        />
                        {errors.criteriaIndustryFocus && (
                          <FormHelperText>
                            <InfoOutlined />
                            {errors.criteriaIndustryFocus.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </div>
                  <div className={classes.group}>
                    <Typography level='body-md'>
                      <b>Have you completed acquisitions in the past?</b>
                    </Typography>
                    <FormControl error={!!errors.hasCompletedAcquisitions}>
                      <Controller
                        name="hasCompletedAcquisitions"
                        control={control}
                        rules={{
                          validate: (value) => value !== null || 'This is required',
                        }}
                        render={({ field }) => (
                          <RadioGroup
                            orientation="horizontal"
                            value={field.value === null ? '' : field.value ? 'yes' : 'no'}
                            onChange={(event) => {
                              const newValue = event.target.value === 'yes';
                              field.onChange(newValue);
                            }}
                          >
                            <Radio value="yes" label="Yes" />
                            <Radio value="no" label="No" />
                          </RadioGroup>
                        )}
                      />
                      {errors.hasCompletedAcquisitions && (
                        <FormHelperText>
                          <InfoOutlined />
                          {errors.hasCompletedAcquisitions.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                    {userInfoForm.watch('hasCompletedAcquisitions') && (
                      <>
                        <Typography level='body-md'>
                          Please list recent acquisitions or provide examples
                          (i.e. company name, sector, year acquired):
                        </Typography>
                        {previousAcquisitionsFields.length === 0 && (
                          <div key="default" style={{ display: 'flex', gap: '8px' }}>
                            <ControlledInput
                              label="Previous Acquisition 1"
                              name="previousAcquisitions.0.value"
                              control={userInfoForm.control}
                              className={classes.fullWidth}
                              required
                            />
                          </div>
                        )}
                        {previousAcquisitionsFields.map((field, index) => (
                          <div key={field.id} className={classes.previousAcquisition}>
                            <ControlledInput
                              label={`Previous Acquisition ${index + 1}`}
                              name={`previousAcquisitions.${index}.value`}
                              control={userInfoForm.control}
                              className={classes.fullWidth}
                              required
                            />
                            {previousAcquisitionsFields.length > 1 && (
                              <IconButton
                                size="lg"
                                variant="outlined"
                                color="danger"
                                onClick={() => removePreviousAcquisition(index)}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </div>
                        ))}
                        <Button
                          startDecorator={<AddIcon />}
                          onClick={() => appendPreviousAcquisition({ value: '' })}
                          variant="outlined"
                          color="success"
                        >
                          Add Previous Acquisition
                        </Button>
                      </>
                    )}
                  </div>
                  <Button type="submit">Next</Button>
                </form>
              </>
            )}
            {
              step === 'ndaRequestInfo' && (
                <>
                  <Typography level='h4'>Experience and Financing</Typography>
                  <Typography level='body-md'>
                    Please provide details about your relevant experience and financial readiness.
                  </Typography>
                  <form
                    onSubmit={ndaRequestForm.handleSubmit(handleNdaRequestSubmit)}
                    className={classes.form}
                  >
                    <div className={classes.group}>
                      <Typography level='body-md'>
                        <b>Do you have relevant experience with similar businesses?</b>
                      </Typography>
                      <FormControl error={!!ndaRequestForm.formState.errors.hasRelevantExperience}>
                        <Controller
                          name="hasRelevantExperience"
                          control={ndaRequestForm.control}
                          rules={{
                            validate: (value) => value !== null || 'This is required',
                          }}
                          render={({ field }) => (
                            <RadioGroup
                              orientation="horizontal"
                              value={field.value === null ? '' : field.value ? 'yes' : 'no'}
                              onChange={(event) => {
                                const newValue = event.target.value === 'yes';
                                field.onChange(newValue);
                              }}
                            >
                              <Radio value="yes" label="Yes" />
                              <Radio value="no" label="No" />
                            </RadioGroup>
                          )}
                        />
                        {ndaRequestForm.formState.errors.hasRelevantExperience && (
                          <FormHelperText>
                            <InfoOutlined />
                            {ndaRequestForm.formState.errors.hasRelevantExperience.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                      {ndaRequestForm.watch('hasRelevantExperience') && (
                        <ControlledInput
                          label="Please briefly describe your experience"
                          name="relevantExperience"
                          control={ndaRequestForm.control}
                          className={classes.fullWidth}
                          error={ndaRequestForm.formState.errors.relevantExperience}
                          textArea
                          required={ndaRequestForm.watch('hasRelevantExperience') || false}
                        />
                      )}
                    </div>
                    <div className={classes.group}>
                      <Typography level='body-md'>
                        <b>Do you have financing secured for this acquisition?</b>
                      </Typography>
                      <Controller
                        name="financingStatus"
                        control={ndaRequestForm.control}
                        rules={{ required: 'This is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <FormControl error={!!error}>
                            <RadioGroup
                              {...field}
                              orientation="horizontal"
                              onChange={(event) => field.onChange(event.target.value)}
                            >
                              {financingStatusOptions.map((option) => (
                                <Radio
                                  key={option.value}
                                  value={option.value}
                                  label={option.label}
                                />
                              ))}
                            </RadioGroup>
                            {error && (
                              <FormHelperText>
                                <InfoOutlined />
                                {error.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                      {
                        ndaRequestForm.watch('financingStatus') !== null &&
                          <>
                            <ControlledInput
                              label={
                                ndaRequestForm.watch('financingStatus') === Type24EnumsListingBuyerNDARequestFinancingStatus.NO ?
                                  'What is your intended funding approach?' :
                                  'What will be the source of funds for this deal?'
                              }
                              name="financingStatusExplanation"
                              control={ndaRequestForm.control}
                              className={classes.fullWidth}
                              error={ndaRequestForm.formState.errors.financingStatusExplanation}
                              inputProps={{
                                placeholder: 'E.g., Personal funds, Institutional financing, Investor commitments',
                              }}
                              textArea
                              required
                            />
                          </>
                      }
                    </div>
                    <Controller
                      name="ownerPostTransactionExpectation"
                      control={ndaRequestForm.control}
                      rules={{ required: 'This is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl error={!!error}>
                          <Typography level='body-md'>
                            <b>
                              Do you want the owner to stay on post-transaction?
                            </b>
                          </Typography>
                          <RadioGroup {...field}>
                            {ownerPostTransactionOptions.map((option) => (
                              <Radio key={option.value} value={option.value} label={option.label} />
                            ))}
                          </RadioGroup>
                          {error && (
                            <FormHelperText>
                              <InfoOutlined />
                              {error.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="referrer"
                      control={ndaRequestForm.control}
                      render={({ field }) => (
                        <FormControl>
                          <Typography level='body-md'>
                            <b>
                              How did you hear about this listing?
                            </b>
                          </Typography>
                          <RadioGroup {...field}>
                            <Radio value="axial" label="Axial" />
                            <Radio value="searchfunder" label="SearchFunder" />
                            <Radio value="linkedin" label="LinkedIn" />
                            <Radio value="bizbuysell" label="BizBuySell" />
                            <Radio value="email" label="Email" />
                            <Radio value="other" label="Other" />
                          </RadioGroup>
                        </FormControl>
                      )}
                    />
                    <Button type="submit">Review and Sign NDA</Button>
                  </form>
                </>
              )
            }
            {step === 'previewNda' && (
              <>
                <Typography level='h4'>Review and Sign NDA</Typography>
                <div className={classes.pdfContainer}>
                  <PDFViewer
                    url={pdfUrl}
                    width={650}
                    showNavigation={true}
                  />
                  <div className={classes.pdfActions}>
                    <Button
                      onClick={handleDownloadNda}
                      variant="outlined"
                      size="lg"
                      color="neutral"
                      startDecorator={<Download />}
                    >
                      Download
                    </Button>
                    <Button
                      onClick={handleSignNda}
                      variant="solid"
                      size="lg"
                      color="primary"
                      startDecorator={<Draw />}
                    >
                      Sign NDA
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {isLoadingSignUrl && (
          <Typography level='body-md'>
            Please wait while we load the NDA signing page...
          </Typography>
        )}
      </ModalDialog>
    </Modal>
  );
}
